import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, SlidersHorizontal, Users } from "lucide-react";
import * as React from "react";

import {
  ApiError,
  getTeamDeals,
  getTeamMembers,
  removeTeamMember,
  resendTeamMemberInvite,
  updateTeamMemberRole,
  type ManagerDealListItem,
  type TeamMemberListItem,
  type TeamMemberRole,
} from "@/api/client";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { TeamMemberDrawer } from "@/components/po-insights/TeamMemberDrawer";
import { statusBadgeVariant } from "@/components/po-insights/team-status";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { SortableHeader } from "@/components/ui/sortable-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  tableRowLinkClassName,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { type SortValue } from "@/lib/tableSort";
import { useTableSort } from "@/lib/useTableSort";
import { cn } from "@/lib/utils";

/** Imperative surface the page's "Find Team Member" CTA drives. */
export interface TeamDirectoryHandle {
  focusSearch: () => void;
}

export interface TeamDirectoryProps {
  token: string;
}

const SEARCH_DEBOUNCE_MS = 300;

/** CHP-480/489: status filter chips. The Insights redesign relabels the
 * status_tab grouping to plain account-state words — Active (registered),
 * Pending (unregistered/invited), Inactive (removed) — with an All reset. */
type StatusChip = "all" | "active" | "pending" | "inactive";
const STATUS_CHIPS: { value: StatusChip; label: string }[] = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
  { value: "inactive", label: "Inactive" },
];

/** Map a member's status_tab onto the chip vocabulary. */
function statusChipFor(m: TeamMemberListItem): Exclude<StatusChip, "all"> {
  switch (m.status_tab) {
    case "registered":
      return "active";
    case "unregistered":
      return "pending";
    case "removed":
    default:
      return "inactive";
  }
}

/** CHP-483: the per-member STATUS badge reads in the chip vocabulary
 * (Active / Pending / Inactive), not the raw backend status_label. Derived from
 * the same grouping the filter chips use so the badge and the chip that selects
 * it always agree. Does NOT mutate the shared STATUS_LABEL — that map is also
 * consumed by the admin users table. */
const STATUS_CHIP_LABEL: Record<Exclude<StatusChip, "all">, string> = {
  active: "Active",
  pending: "Pending",
  inactive: "Inactive",
};
function statusBadgeLabel(m: TeamMemberListItem): string {
  // CHP-958: an invited-but-not-yet-onboarded member reads "Invited" here, matching
  // the admin surfaces (users-table, UserDetailSheet) that already render STATUS_LABEL
  // .invited. This overrides CHP-483's blanket "Pending" for the invited sub-case only —
  // the PO reported the mismatch in UAT (Deals V2 AC-01) and cross-surface consistency
  // won. The status_tab grouping is untouched, so the member still FILTERS under the
  // Pending chip (invited ⊂ unregistered); only the badge label diverges.
  if (m.status === "invited") return "Invited";
  return STATUS_CHIP_LABEL[statusChipFor(m)];
}

/** Review-r2: the role filter is a multi-select set of checkboxes (was a
 * single-select dropdown). An empty set means "no role filter" (show all). */
const ROLE_FILTER_OPTIONS: { value: TeamMemberRole; label: string }[] = [
  { value: "po", label: "Program Owner" },
  { value: "bdm", label: "BDM" },
];

/** Sortable columns for the directory table (CHP-485/486). */
type DirectorySortColumn = "name" | "role" | "account" | "live_deals";

/** Pull a structured `{code:"last_po", message}` 409 detail out of an ApiError;
 * returns the message when present, else null. */
function lastPoMessage(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body;
  if (body && typeof body === "object" && "detail" in body) {
    const detail = (body as { detail: unknown }).detail;
    if (detail && typeof detail === "object" && (detail as { code?: string }).code === "last_po") {
      return (
        (detail as { message?: string }).message ??
        "This is your organisation's only Program Owner. Promote another member first."
      );
    }
  }
  return null;
}

/** Best-effort plain-string detail from an ApiError body, for terse alerts. */
function stringDetail(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body;
  if (body && typeof body === "object" && "detail" in body) {
    const detail = (body as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;
  }
  return null;
}

function memberName(m: TeamMemberListItem): string {
  return (
    m.full_name?.trim() ||
    [m.first_name, m.last_name].filter(Boolean).join(" ") ||
    m.email ||
    "Unnamed member"
  );
}

/**
 * Team Directory (Insights & Admin Flow F) — the PO's persistent Team
 * Management Workspace, restyled to match the deals-dashboard ledger (CHP-480…
 * 498). A full-width sortable table of the PO's own org members:
 *
 *   Name · Role · Account · # Live deals · Actions
 *
 * Status chips (All / Active / Pending / Inactive) filter on the member's
 * account state; the role filter lives in an Advanced-Filters panel (mirroring
 * the deals ledger). The whole row opens the member profile slide-out; the
 * per-row Manage menu (role edit / remove / resend-invite) stays independently
 * clickable via stopPropagation.
 *
 * The page mounts this below the metric cards and holds a ref so its "Find Team
 * Member" header action can scroll to + focus the search box.
 */
export const TeamDirectory = React.forwardRef<TeamDirectoryHandle, TeamDirectoryProps>(
  function TeamDirectory({ token }, ref) {
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const searchRef = React.useRef<HTMLInputElement>(null);
    const sectionRef = React.useRef<HTMLDivElement>(null);
    const filtersRef = React.useRef<HTMLDivElement>(null);

    React.useImperativeHandle(ref, () => ({
      focusSearch() {
        sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        searchRef.current?.focus();
      },
    }));

    const membersQuery = useQuery<TeamMemberListItem[]>({
      queryKey: ["partners", "team-members", token],
      queryFn: () => getTeamMembers(token),
      enabled: !!token,
    });

    // CHP-498: "# Live deals" per member counts org-wide LIVE deals owned by
    // that member (lead_bdm_id === user_id). Reuses the same org-scoped feed
    // the Deal Explorer drawer loads, so the count matches the explorer.
    const dealsQuery = useQuery<ManagerDealListItem[]>({
      queryKey: ["deals", "team-list", "all", token],
      queryFn: () => getTeamDeals(token, "all"),
      enabled: !!token,
    });

    const liveDealsByOwner = React.useMemo(() => {
      const counts = new Map<string, number>();
      for (const d of dealsQuery.data ?? []) {
        if (d.deal_status !== "live" || !d.lead_bdm_id) continue;
        counts.set(d.lead_bdm_id, (counts.get(d.lead_bdm_id) ?? 0) + 1);
      }
      return counts;
    }, [dealsQuery.data]);

    const [statusChip, setStatusChip] = React.useState<StatusChip>("all");
    // Review-r2: role filter is now a multi-select set; empty = no filter.
    const [roleFilters, setRoleFilters] = React.useState<Set<TeamMemberRole>>(new Set());
    const [filtersOpen, setFiltersOpen] = React.useState(false);
    const [searchInput, setSearchInput] = React.useState("");
    const [debouncedSearch, setDebouncedSearch] = React.useState("");
    const [detailUserId, setDetailUserId] = React.useState<string | null>(null);

    // The member targeted by an open Manage action (role-edit / remove), null
    // when no dialog is open.
    const [roleEdit, setRoleEdit] = React.useState<TeamMemberListItem | null>(null);
    const [removeTarget, setRemoveTarget] = React.useState<TeamMemberListItem | null>(null);
    // The last-PO 409 message, surfaced as a styled warning dialog.
    const [warning, setWarning] = React.useState<string | null>(null);

    React.useEffect(() => {
      const t = window.setTimeout(() => setDebouncedSearch(searchInput), SEARCH_DEBOUNCE_MS);
      return () => window.clearTimeout(t);
    }, [searchInput]);

    // Close the advanced-filters panel on click-outside (mirrors DealAdvancedFilters).
    React.useEffect(() => {
      if (!filtersOpen) return;
      function onPointerDown(e: PointerEvent) {
        if (filtersRef.current && !filtersRef.current.contains(e.target as Node)) {
          setFiltersOpen(false);
        }
      }
      document.addEventListener("pointerdown", onPointerDown);
      return () => document.removeEventListener("pointerdown", onPointerDown);
    }, [filtersOpen]);

    function refetchAfterMutation(userId: string) {
      void queryClient.invalidateQueries({ queryKey: ["partners", "team-members"] });
      void queryClient.invalidateQueries({ queryKey: ["partners", "team-member", userId] });
    }

    const roleMut = useMutation({
      mutationFn: ({ userId, role }: { userId: string; role: TeamMemberRole }) =>
        updateTeamMemberRole(token, userId, role),
      onSuccess: (_data, variables) => {
        refetchAfterMutation(variables.userId);
        setRoleEdit(null);
      },
      onError: (err: unknown) => {
        const lastPo = lastPoMessage(err);
        if (lastPo) {
          setRoleEdit(null);
          setWarning(lastPo);
          return;
        }
        window.alert(stringDetail(err) ?? "Could not change the access level. Try again.");
      },
    });

    const removeMut = useMutation({
      mutationFn: ({ userId, note }: { userId: string; note?: string }) =>
        removeTeamMember(token, userId, note),
      onSuccess: (_data, variables) => {
        refetchAfterMutation(variables.userId);
        setRemoveTarget(null);
      },
      onError: (err: unknown) => {
        const lastPo = lastPoMessage(err);
        if (lastPo) {
          setRemoveTarget(null);
          setWarning(lastPo);
          return;
        }
        window.alert(stringDetail(err) ?? "Could not remove this member. Try again.");
      },
    });

    // CHP-498: re-send the set-password invite to a not-yet-onboarded member.
    const resendMut = useMutation({
      mutationFn: (userId: string) => resendTeamMemberInvite(token, userId),
      onSuccess: (_data, userId) => {
        refetchAfterMutation(userId);
        toast({ variant: "success", description: "Invitation re-sent." });
      },
      onError: (err: unknown) => {
        toast({
          variant: "destructive",
          description: stringDetail(err) ?? "Couldn't re-send the invitation. Try again.",
        });
      },
    });

    const query = debouncedSearch.trim().toLowerCase();
    const visible = React.useMemo(() => {
      const rows = membersQuery.data ?? [];
      return rows.filter((m) => {
        if (statusChip !== "all" && statusChipFor(m) !== statusChip) return false;
        // Empty role set = no filter; otherwise the member's role must be selected.
        if (roleFilters.size > 0 && !roleFilters.has(m.role)) return false;
        if (query !== "") {
          // Email stays searchable even though the column was dropped (CHP-483).
          const haystack = `${memberName(m)} ${m.email ?? ""}`.toLowerCase();
          if (!haystack.includes(query)) return false;
        }
        return true;
      });
    }, [membersQuery.data, statusChip, roleFilters, query]);

    const liveDealCount = React.useCallback(
      (m: TeamMemberListItem) => liveDealsByOwner.get(m.user_id) ?? 0,
      [liveDealsByOwner],
    );

    const sortValue = React.useCallback(
      (m: TeamMemberListItem, column: DirectorySortColumn): SortValue => {
        switch (column) {
          case "name":
            return memberName(m);
          case "role":
            return m.role_label;
          case "account":
            // CHP-483/CHP-958: sort by the displayed badge label (chip vocabulary,
            // except invited members read "Invited") so the Account column orders as
            // shown.
            return statusBadgeLabel(m);
          case "live_deals":
            return liveDealCount(m);
        }
      },
      [liveDealCount],
    );

    const { sort, toggle, sortedRows } = useTableSort(visible, sortValue);

    const emptyMessage =
      query !== "" || roleFilters.size > 0 || statusChip !== "all"
        ? "No team members match your filters."
        : "No team members yet.";

    function toggleRoleFilter(role: TeamMemberRole) {
      setRoleFilters((prev) => {
        const next = new Set(prev);
        if (next.has(role)) next.delete(role);
        else next.add(role);
        return next;
      });
    }

    return (
      <section ref={sectionRef} className="mt-10" data-testid="team-directory">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Team Access &amp; Activity</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Manage account permissions and track team activity.
            </p>
          </div>
        </div>

        {/* Status chips + advanced (role) filter + search */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div
            className="flex flex-wrap items-center gap-2"
            role="group"
            aria-label="Filter by account status"
          >
            {STATUS_CHIPS.map((chip) => (
              <Button
                key={chip.value}
                type="button"
                size="sm"
                variant={statusChip === chip.value ? "default" : "outline"}
                onClick={() => setStatusChip(chip.value)}
                data-testid={`team-directory-tab-${chip.value}`}
                title={chip.value === "pending" ? "Invite not accepted yet" : undefined}
              >
                {chip.label}
              </Button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Review-r2: the search bar leads; Advanced filters sits to its
                RIGHT (the cross-page convention — filters always right of
                search). */}
            <div className="relative w-full sm:w-72">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                ref={searchRef}
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by name…"
                aria-label="Search team members"
                className="pl-9"
                data-testid="team-directory-search"
              />
            </div>

            {/* CHP-482: the role filter lives in an Advanced-Filters panel,
                mirroring the deals ledger's idiom. Review-r2: roles are now
                CHECKBOXES (multi-select), not a single-select dropdown. */}
            <div className="relative" ref={filtersRef}>
              <Button
                type="button"
                variant="outline"
                onClick={() => setFiltersOpen((o) => !o)}
                aria-expanded={filtersOpen}
                data-testid="team-directory-filters-toggle"
              >
                <SlidersHorizontal className="h-4 w-4" aria-hidden />
                Advanced filters
                {roleFilters.size > 0 ? (
                  <span
                    className="ml-1 rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-foreground tabular-nums"
                    data-testid="team-directory-filters-count"
                  >
                    {roleFilters.size}
                  </span>
                ) : null}
              </Button>

              {filtersOpen ? (
                <div
                  className="absolute right-0 z-20 mt-2 w-[min(20rem,90vw)] space-y-4 rounded-md border bg-popover p-4 shadow-md"
                  data-testid="team-directory-filters-panel"
                >
                  <fieldset>
                    <legend className="mb-2 text-sm font-semibold">Role</legend>
                    <div className="space-y-2">
                      {ROLE_FILTER_OPTIONS.map((opt) => (
                        <label
                          key={opt.value}
                          className="flex items-center gap-2 text-sm"
                          htmlFor={`team-directory-role-${opt.value}`}
                        >
                          <input
                            id={`team-directory-role-${opt.value}`}
                            type="checkbox"
                            className="h-4 w-4 rounded border-input"
                            checked={roleFilters.has(opt.value)}
                            onChange={() => toggleRoleFilter(opt.value)}
                            data-testid={`team-directory-role-${opt.value}`}
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={roleFilters.size === 0}
                    onClick={() => setRoleFilters(new Set())}
                    data-testid="team-directory-filters-clear"
                  >
                    Clear all
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="mt-4 overflow-x-auto">
          {membersQuery.isLoading ? (
            <p role="status" className="text-sm text-muted-foreground">
              Loading team…
            </p>
          ) : membersQuery.error ? (
            <p role="alert" className="text-sm">
              Couldn&apos;t load your team.
            </p>
          ) : sortedRows.length === 0 ? (
            <EmptyState title={emptyMessage} icon={Users} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader column="name" sort={sort} onToggle={toggle}>
                    Name
                  </SortableHeader>
                  <SortableHeader column="role" sort={sort} onToggle={toggle}>
                    Role
                  </SortableHeader>
                  <SortableHeader column="account" sort={sort} onToggle={toggle}>
                    Account
                  </SortableHeader>
                  <SortableHeader column="live_deals" sort={sort} onToggle={toggle} align="right">
                    # Live deals
                  </SortableHeader>
                  <TableHead className="text-right">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedRows.map((m) => {
                  // A PO acting on themselves can edit their own role but not
                  // self-remove (422 server-side); disable Manage entirely for
                  // a removed member (already non-active).
                  const isRemoved = m.status_tab === "removed";
                  // CHP-498: a not-yet-onboarded member can be re-invited.
                  const canResend = m.status_tab === "unregistered";
                  return (
                    <TableRow
                      interactive
                      key={m.user_id}
                      data-testid={`team-member-row-${m.user_id}`}
                      onClick={() => setDetailUserId(m.user_id)}
                    >
                      <TableCell>
                        {/* Primary control — keyboard-focusable and named; the
                            row onClick above is the cross-browser pointer path.
                            stopPropagation keeps the (visual-only) overlay from
                            double-firing the open handler (same pattern as
                            DealsDataGrid / PoDealLedger). */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDetailUserId(m.user_id);
                          }}
                          className={cn("font-medium text-foreground", tableRowLinkClassName)}
                          data-testid={`team-member-name-${m.user_id}`}
                        >
                          {memberName(m)}
                        </button>
                        {m.is_self ? (
                          <span className="ml-2 text-xs text-muted-foreground">(you)</span>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {/* Review-r2: role is plain text, not a pill. */}
                        <span className="text-sm">{m.role_label}</span>
                      </TableCell>
                      <TableCell>
                        {/* CHP-490: status badge restored to the shared mapping
                            (active=success); the de-green targeted the ROLE, not
                            this Account/STATUS column. CHP-483: label reads in the
                            chip vocabulary (Active/Pending/Inactive). */}
                        <Badge variant={statusBadgeVariant(m.status)} dot>
                          {statusBadgeLabel(m)}
                        </Badge>
                      </TableCell>
                      <TableCell
                        className="text-right tabular-nums"
                        data-testid={`team-member-live-deals-${m.user_id}`}
                      >
                        {liveDealCount(m)}
                      </TableCell>
                      <TableCell className="text-right">
                        {/* In-row control: a stopPropagation wrapper (click +
                            pointerdown — Radix triggers open on pointerdown)
                            keeps the menu independently clickable without the
                            row's open handler firing. relative z-10 lifts it
                            above the row's pointer-events overlay. */}
                        <div
                          className="relative z-10 inline-flex"
                          onClick={(e) => e.stopPropagation()}
                          onPointerDown={(e) => e.stopPropagation()}
                        >
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={isRemoved}
                                aria-label={`Manage ${memberName(m)}`}
                                data-testid={`team-member-manage-${m.user_id}`}
                              >
                                Manage ▾
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onSelect={() => setRoleEdit(m)}>
                                Edit role
                              </DropdownMenuItem>
                              {canResend ? (
                                <DropdownMenuItem
                                  data-testid={`team-member-resend-${m.user_id}`}
                                  onSelect={() => resendMut.mutate(m.user_id)}
                                >
                                  Resend invitation
                                </DropdownMenuItem>
                              ) : null}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                // Self-removal 422s server-side; suppress it here.
                                disabled={m.is_self}
                                onSelect={() => {
                                  if (m.is_self) return;
                                  setRemoveTarget(m);
                                }}
                              >
                                Remove member…
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Member profile slide-out */}
        <TeamMemberDrawer
          userId={detailUserId}
          token={token}
          onClose={() => setDetailUserId(null)}
        />

        {/* Edit role — flip BDM↔PO. The target is the opposite of the current. */}
        {roleEdit
          ? (() => {
              const target: TeamMemberRole = roleEdit.role === "po" ? "bdm" : "po";
              const targetLabel = target === "po" ? "Program Owner" : "BDM";
              return (
                <ConfirmDialog
                  open
                  onOpenChange={(o) => {
                    if (!o) setRoleEdit(null);
                  }}
                  title="Change access level"
                  description={
                    <>
                      Change <strong>{memberName(roleEdit)}</strong> from{" "}
                      {roleEdit.role === "po" ? "Program Owner" : "BDM"} to{" "}
                      <strong>{targetLabel}</strong>?
                      {target === "po"
                        ? " Program Owners can manage your whole organisation."
                        : " BDMs can only register and manage their own deals."}
                    </>
                  }
                  confirmLabel={`Make ${targetLabel}`}
                  pending={roleMut.isPending}
                  onConfirm={() => roleMut.mutate({ userId: roleEdit.user_id, role: target })}
                />
              );
            })()
          : null}

        {/* Remove member — deactivate (never hard-delete), optional note. */}
        {removeTarget ? (
          <ConfirmDialog
            open
            onOpenChange={(o) => {
              if (!o) setRemoveTarget(null);
            }}
            title="Remove team member"
            description={
              <>
                Remove <strong>{memberName(removeTarget)}</strong> from your organisation? This
                deactivates their account — they will no longer be able to sign in. Open deals and
                SPIFs stay attached for ResetData staff to reassign.
              </>
            }
            confirmLabel="Remove member"
            variant="destructive"
            reason={{
              label: "Note (optional)",
              placeholder: "Add a note for the audit trail…",
              required: false,
              maxLength: 500,
            }}
            pending={removeMut.isPending}
            onConfirm={(note) =>
              removeMut.mutate({ userId: removeTarget.user_id, note: note || undefined })
            }
          />
        ) : null}

        {/* Last-PO guard — a styled warning when the action would strand the
            org with no Program Owner. */}
        {warning ? (
          <Dialog
            open
            onOpenChange={(o) => {
              if (!o) setWarning(null);
            }}
          >
            <DialogContent data-testid="team-directory-last-po-warning">
              <DialogHeader>
                <DialogTitle>Your organisation needs a Program Owner</DialogTitle>
                <DialogDescription>{warning}</DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button type="button" onClick={() => setWarning(null)}>
                  Got it
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        ) : null}
      </section>
    );
  },
);
